import React, { useState } from 'react';
import { BookOpen, Lock, Shield, AlertTriangle, ArrowRight, Key } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import GoogleButton from './GoogleButton';
import {
  productConfig, validateLicenceFn, googleSignInResult, googleErrorMessage, entitles, normaliseScope,
  goToPostSignInDestination,
} from './licenceApi';

/* Product-aware licence gate: Google sign-in (primary) + licence-key fallback.
   Identical for the Roadmap and the Planner; `product` picks the copy, the cache
   key, and which entitlement a typed key must satisfy. */
export default function LicenceGate({ product = 'roadmap', onUnlock, onShowPlans, onGoogleNoLicence, checkoutStatus }) {
  const cfg = productConfig(product);
  const [key, setKey]     = useState('');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');
  const [errMsg, setErrMsg] = useState('');
  const [googleBusy, setGoogleBusy] = useState(false);
  const [googleErr, setGoogleErr]   = useState('');

  const continueWithGoogle = async () => {
    setGoogleErr(''); setErrMsg(''); setGoogleBusy(true);
    try {
      const r = await googleSignInResult(product);
      // An ENTITLED account lands on the hub — sign-in at any door converges
      // there. An UNENTITLED one must stay put: they arrived to buy or start a
      // trial, and bouncing them to a hub of locked cards would lose that
      // journey. Deep-linking this tool while already signed in never reaches
      // here at all; that runs through useLicence's auto-unlock.
      if (r.unlocked && r.user) { await goToPostSignInDestination(r.user); return; }
      if (r.unlocked) { onUnlock(r.data, r.expired); return; }
      onGoogleNoLicence(r.email);           // signed in, no licence → pre-filled trial
    } catch (e) {
      setGoogleErr(googleErrorMessage(e));
    } finally { setGoogleBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!key.trim() || !email.trim()) return;
    setStatus('checking'); setErrMsg('');
    try {
      const res = await validateLicenceFn({ key: key.trim().toUpperCase(), email: email.trim().toLowerCase() });
      if (!res.data?.valid) { setStatus('error'); setErrMsg('Licence key not found or inactive. Please double-check your key and the email used at purchase.'); return; }
      const scope = normaliseScope(res.data?.licence?.scope);
      if (!entitles(scope, product)) {
        setStatus('error');
        setErrMsg(`That key is for a different Narratoria tool, not the ${cfg.noun}. Check you're using the right key, or view plans below.`);
        return;
      }
      const data = {
        key: key.trim().toUpperCase(),
        email: email.trim().toLowerCase(),
        term: res.data?.licence?.term || null,
        scope,
        expiresAtMs: res.data?.licence?.expiresAtMs ?? null,
      };
      localStorage.setItem(cfg.cacheKey, JSON.stringify(data));
      onUnlock(data, res.data?.expired);
    } catch { setStatus('error'); setErrMsg('Could not verify — please check your connection and try again.'); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-ink-900 via-ink to-ink-900" style={{fontFamily:"'Cormorant Garamond',Georgia,serif"}}>
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 h-96 w-96 rounded-full bg-gold/10 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 h-96 w-96 rounded-full bg-gold-deep/10 blur-3xl" />
      </div>
      <div className="relative w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gold shadow-2xl shadow-black/50">
            <BookOpen className="h-8 w-8 text-white" />
          </div>
          <h1 className="font-cinzel text-3xl font-bold text-white">{cfg.gateTitle}<br /><span className="text-gold">{cfg.gateTitleAccent}</span></h1>
          <p className="mt-2 text-sm text-parchment/50">by Narratoria Ltd</p>
        </div>
        {checkoutStatus === 'success' && (
          <div className="mb-4 rounded-xl border border-green-500/30 bg-green-500/10 p-3 text-center">
            <p className="text-xs text-green-300">Payment received. Your licence key is on its way by email — enter it below to unlock (or sign in to unlock automatically).</p>
          </div>
        )}
        {checkoutStatus === 'cancel' && (
          <div className="mb-4 rounded-xl border border-parchment/20 bg-parchment/5 p-3 text-center">
            <p className="text-xs text-parchment/70">Checkout cancelled — no charge was made.</p>
          </div>
        )}
        <div className="overflow-hidden rounded-2xl border border-white/10 bg-white/5 shadow-2xl backdrop-blur-sm">
          <div className="flex items-center gap-3 border-b border-white/10 bg-gold/5 px-6 py-4">
            <Lock className="h-5 w-5 text-gold" />
            <div><p className="text-sm font-semibold text-white">{cfg.signInHeading}</p><p className="text-xs text-parchment/50">Continue with Google, or use your licence key</p></div>
          </div>
          <div className="p-6 pb-0">
            <GoogleButton onClick={continueWithGoogle} busy={googleBusy} />
            <p className="mt-2 text-center text-xs text-parchment/40">Signing in unlocks automatically if your email has a licence.</p>
            {googleErr && (
              <div className="mt-3 flex items-start gap-2 rounded-xl border border-rose-400/40 bg-rose-500/10 p-3">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-300" /><p className="text-xs text-rose-200">{googleErr}</p>
              </div>
            )}
            <div className="mt-5 flex items-center gap-3 text-xs uppercase tracking-widest text-parchment/40">
              <span className="h-px flex-1 bg-white/10" /> or enter your licence key <span className="h-px flex-1 bg-white/10" />
            </div>
          </div>
          <form onSubmit={submit} className="space-y-4 p-6">
            <div>
              <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-widest text-parchment/50">Licence Key</Label>
              <div className="relative">
                <Key className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-parchment/40" />
                <Input type="text" value={key} onChange={e => setKey(e.target.value.toUpperCase())} placeholder="NCC-XXXX-XXXX-XXXX" autoComplete="off" spellCheck={false}
                  className="w-full rounded-xl border border-white/10 bg-white/10 py-3 pl-10 pr-4 font-mono text-sm text-white placeholder-parchment/40 outline-none focus:border-gold focus:ring-2 focus:ring-gold/30"
                  style={{userSelect:'text',WebkitUserSelect:'text'}} />
              </div>
            </div>
            <div>
              <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-widest text-parchment/50">Purchase Email</Label>
              <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="the email used at checkout"
                className="w-full rounded-xl border border-white/10 bg-white/10 px-4 py-3 text-sm text-white placeholder-parchment/40 outline-none focus:border-gold focus:ring-2 focus:ring-gold/30"
                style={{userSelect:'text',WebkitUserSelect:'text'}} />
            </div>
            {errMsg && (
              <div className="flex items-start gap-2 rounded-xl border border-red-500/30 bg-red-500/10 p-3">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-400" /><p className="text-xs text-red-300">{errMsg}</p>
              </div>
            )}
            <Button type="submit" disabled={status==='checking'||!key.trim()||!email.trim()}
              className="group flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-gold to-gold-deep px-4 py-3.5 text-sm font-bold text-ink shadow-lg shadow-black/40 transition-all hover:from-gold-light hover:to-gold disabled:opacity-50 disabled:cursor-not-allowed">
              {status==='checking' ? <><span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white"/>Verifying…</> : <>Unlock My {cfg.noun}<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1"/></>}
            </Button>
          </form>
          <div className="flex items-center gap-2 border-t border-white/5 px-6 py-4">
            <Shield className="h-3.5 w-3.5 shrink-0 text-parchment/40" />
            <span className="text-xs text-parchment/50">Licence is tied to your purchase email and is non-transferable.</span>
          </div>
        </div>
        <p className="mt-6 text-center text-xs text-parchment/40">Don't have a licence? <button type="button" onClick={onShowPlans} className="text-parchment/60 underline hover:text-gold">View plans →</button></p>
      </div>
    </div>
  );
}
